import React, { useState, useRef } from 'react';
import { Divider, Menu, Dropdown, Button, Popover } from 'antd';
import PropTypes from 'prop-types';

import { ALIGNMENT_STYLES, BLOCK_TYPES, LINE_STYLES } from 'pages/Proposal/constants/constants';

import LeftAlignIcon from 'components/Icons/LeftAlignIcon';
import DropdownIcon from 'components/Icons/DropdownIcon';
import LinkIcon from 'components/Icons/LinkIcon';
import LineSpacingIcon from 'components/Icons/LineSpacingIcon';
import LeftIntentIcon from 'components/Icons/LeftIntentIcon';
import RightIntentIcon from 'components/Icons/RightIntentIcon';
import ClearFormattingIcon from 'components/Icons/ClearFormattingIcon';
import ListIcon from 'components/Icons/ListIcon';
import QuotesIcon from 'components/Icons/QuotesIcon';
import SplitSectionIcon from 'components/Icons/SplitSectionIcon';
import BoldIcon from 'components/Icons/BoldIcon';
import ItalicIcon from 'components/Icons/ItalicIcon';
import UnderlineIcon from 'components/Icons/UnderlineIcon';
import StrikeThroughIcon from 'components/Icons/StrikeThroughIcon';
import MenuSmallIcon from 'components/Icons/MenuSmallIcon';
import OverflowDropDownList from 'components/OverflowDropDownList/OverflowDropDownList';
import InputNumber from 'components/InputNumber';
import FontTools from './FontTools';
import ToolbarTooltipItem from './ToolbarTooltipItem';
import useWindowDimensions from 'hooks/useWindowDimensions';

const SMALLER_WIDTH = 992;

const ExternalToolbar = ({
  language,
  bodyFont,
  editorState,
  onToggle,
  onToggleBlockType,
  onToggleCustomBockStyle,
  onToggleFontInlineStyle,
  onClearFormatting,
  onSplitSection,
  dispatch,
  selectionActive,
  user,
  displayColorPicker,
  colorPicker,
  isSection,
  hideTools,
  fallbackStyles,
  lineHeightDropDownVisible,
  toggleLineHeightDropDown,
  scrollToolbar,
}) => {
  const { width } = useWindowDimensions();
  const isSmaller = width < SMALLER_WIDTH;

  const inputNumberRef = useRef(null);
  const [lineHeightValue, setLineHeightValue] = useState(0);

  const selection = editorState.getSelection();
  const block = editorState.getCurrentContent().getBlockForKey(selection.getStartKey());
  const blockType = (block && block.getType()) || '';
  const blockData = (block && block.getData()) || '';
  const blockClassName = blockData && blockData.get('className');
  const currentStyle = block ? editorState?.getCurrentInlineStyle() : '';

  let color = currentStyle
    ? currentStyle?.filter((value) => value?.toString()?.startsWith('rgba'))?.last()
    : null;
  if (!color) {
    color = fallbackStyles?.color || '#4a4a4a';
  }

  // selected text's background color [starts with bg-rgba()]
  let backgroundColor = currentStyle
    ? currentStyle?.filter((value) => value?.toString()?.startsWith('bg-'))?.last() ||
      'rgba(255,255,255,1)'
    : 'rgba(255,255,255,1)';
  if (backgroundColor?.startsWith('bg-')) {
    backgroundColor = backgroundColor.substring(3);
  }
  // REMARK
  // if the selected range contains red and blue text it will show the color that comes first
  // google doc dont show  any color in this case

  let direction = '';
  const directionBlockType = blockType?.match('editor-direction');
  if (directionBlockType) {
    if (blockType.match(/ltr/)) direction = 'left';
    else if (blockType.match(/rtl/)) direction = 'right';
  }

  const menuLineSpacingList = () => {
    const spacingSelectedKeys = [];
    LINE_STYLES.forEach((lineStyle) => {
      if (blockType.match(lineStyle.style)) spacingSelectedKeys.push(lineStyle.style);
    });

    return (
      <Menu
        onMouseLeave={() => {
          dispatch({ type: 'setLineHeightInput', lineHeightInput: false });
        }}
        selectedKeys={spacingSelectedKeys}
        className="rich-editor-intent-list-option">
        {LINE_STYLES.map((type) => (
          <Menu.Item
            key={type.style}
            onMouseDown={(e) => {
              e.preventDefault();
              onToggleCustomBockStyle({
                blockType: type.style,
                type: 'height',
              });
            }}>
            {type.text}
          </Menu.Item>
        ))}
        <Menu.Item>
          <Divider type="vertical" />
        </Menu.Item>

        <Menu.Item key={'custom_line_height'}>
          <InputNumber
            value={lineHeightValue}
            type="number"
            placeholder={1.0}
            min={0}
            max={18}
            step={0.25}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (inputNumberRef.current) {
                inputNumberRef.current.focus();
              }
            }}
            ref={inputNumberRef}
            onChange={(e) => {
              let value = parseFloat(e.target.value);
              let lineHeight = value.toFixed(1);
              if (!isNaN(value)) {
                value = Math.min(Math.max(value, 0), 18);
              }
              if (lineHeight) {
                lineHeight = lineHeight.replace(/\./, '-');
                setLineHeightValue(value);
                onToggleCustomBockStyle({
                  blockType: `line-height__${lineHeight}`,
                  type: 'height',
                });
              }
            }}
            onHandleChange={(value) => {
              let lineHeight = value.toFixed(1);
              if (lineHeight) {
                lineHeight = lineHeight.replace(/\./, '-');
                setLineHeightValue(value);
                onToggleCustomBockStyle({
                  blockType: `line-height__${lineHeight}`,
                  type: 'height',
                });
              }
            }}
          />
        </Menu.Item>
      </Menu>
    );
  };

  const SPACING_STYLES = [
    {
      style: 'line-height',
      text: 'Line height',
      icon: (
        <Dropdown
          // onVisibleChange={(visible) => toggleLineHeightDropDown(visible)}
          // visible={lineHeightDropDownVisible}
          overlay={menuLineSpacingList}
          trigger={['click']}
          overlayClassName="editor-toolbar-dropdown"
          className="rich-editor-list">
          <Button
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // toggleLineHeightDropDown(true);
            }}>
            <LineSpacingIcon />
            <DropdownIcon className="rich-editor-dropdown-icon" />
          </Button>
        </Dropdown>
      ),
    },
    {
      style: 'left-intent',
      text: 'Left indent',
      icon: <LeftIntentIcon />,
    },
    {
      style: 'right-intent',
      text: 'Right indent',
      icon: <RightIntentIcon />,
    },
  ];

  const menuList = (
    <Menu
      selectedKeys={[blockClassName || blockType]}
      className="rich-editor-list-option list-item">
      {BLOCK_TYPES.map((type) => (
        <Menu.Item
          key={type.style}
          icon={type.icon}
          onMouseDown={(e) => {
            e.preventDefault();
            onToggleBlockType(type.style, type.custom, null, blockClassName || blockType);
          }}
        />
      ))}
    </Menu>
  );

  const alignmentMenuList = (
    <div className="rich-editor-list-option list-item alignment-menu">
      {ALIGNMENT_STYLES.map((style) => (
        <ToolbarTooltipItem key={style.text} title={style.text}>
          <div
            className={`rich-editor-toolbar-icon ${
              !!blockType && blockType.match(style.style) ? 'true' : ''
            } ${
              style.type === 'editor-direction' && direction
                ? `editor-toolbar-direction-${direction}`
                : ''
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              onToggleCustomBockStyle({
                blockType: style.style,
                type: style.type,
              });
            }}>
            {style.type === 'editor-direction' ? React.cloneElement(style.icon) : style.icon}
          </div>
        </ToolbarTooltipItem>
      ))}
    </div>
  );

  const INLINE_STYLES = [
    { style: 'BOLD', text: 'Bold', icon: <BoldIcon /> },
    { style: 'ITALIC', text: 'Italic', icon: <ItalicIcon /> },
    { style: 'UNDERLINE', text: 'Underline', icon: <UnderlineIcon /> },
    {
      style: 'STRIKETHROUGH',
      text: 'Strikethrough',
      icon: <StrikeThroughIcon />,
    },
    {
      style: 'blockquote',
      text: 'Quotes',
      icon: <QuotesIcon />,
    },
    {
      style: 'COLOR',
      text: 'Text color',
      icon: (
        <div className="color-block-wrapper">
          {isSmaller ? (
            <Popover
              overlayClassName="color-picker-popover"
              placement="bottom"
              onClick={(e) => e.stopPropagation()}
              trigger="click"
              content={displayColorPicker && colorPicker}>
              <div className="color-block" style={{ backgroundColor: color }} />
            </Popover>
          ) : (
            <div className="color-block" style={{ backgroundColor: color }} />
          )}
        </div>
      ),
    },
    {
      style: 'BACKGROUND',
      text: 'Background color',
      icon: (
        <div className="color-block-wrapper">
          {isSmaller ? (
            <Popover
              overlayClassName="color-picker-popover"
              placement="bottom"
              onClick={(e) => e.stopPropagation()}
              trigger="click"
              content={displayColorPicker && colorPicker}>
              <div className="color-block" style={{ backgroundColor: backgroundColor }} />
            </Popover>
          ) : (
            <div className="color-block" style={{ backgroundColor: backgroundColor }} />
          )}
        </div>
      ),
    },
  ];

  return (
    <OverflowDropDownList
      dropdownClassName="rich-editor-toolbar-overflow-dropdown"
      dropdownOverlayClassName="rich-editor-toolbar-overflow-dropdown-overlay"
      dropdownDynamic={false}
      dropdownPlacement="bottomLeft"
      dropdownIcon={<MenuSmallIcon className="rich-editor-toolbar-overflow-dropdown-trigger" />}
      dropdownOverFlowLimit={7}
      type="toolbar"
      listItems={[
        <FontTools
          bodyFont={bodyFont}
          language={language?.toLowerCase()}
          editorState={editorState}
          onToggleFontInlineStyle={onToggleFontInlineStyle}
          user={user}
        />,
        <>
          {INLINE_STYLES.map((type) => (
            <ToolbarTooltipItem title={type.text} key={type.style}>
              <a
                className={`rich-editor-toolbar-icon ${type.style} ${
                  (currentStyle && currentStyle?.has(type.style)) ||
                  blockType.search(type.style) > -1
                    ? 'true'
                    : ''
                }`}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  type.style === 'blockquote'
                    ? onToggleBlockType('blockquote')
                    : onToggle({ inlineStyle: type.style });
                }}>
                {type.icon}
              </a>
            </ToolbarTooltipItem>
          ))}

          {!isSmaller && <Divider type="vertical">{displayColorPicker && colorPicker}</Divider>}
        </>,

        <>
          <ToolbarTooltipItem title="Insert Link">
            <a
              className={`rich-editor-toolbar-icon ${currentStyle && currentStyle.has('link')}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onToggle({
                  withoutText: selection.isCollapsed() ? true : false,
                  inlineStyle: 'LINK',
                });
              }}>
              <LinkIcon fill="#05034d" />
            </a>
          </ToolbarTooltipItem>

          <ToolbarTooltipItem title="List">
            <Dropdown
              placement="bottomLeft"
              overlay={menuList}
              trigger={['click']}
              overlayClassName="list-item-dropdown editor-toolbar-dropdown"
              className="rich-editor-list list-item">
              <Button
                className={!!blockType && blockType.match(/list-item/) ? 'toolbar-icon-active' : ''}
                onMouseDown={(e) => e.preventDefault()}>
                <ListIcon />
                <DropdownIcon className="rich-editor-dropdown-icon" />
              </Button>
            </Dropdown>
          </ToolbarTooltipItem>
          {!isSmaller && <Divider type="vertical" />}
        </>,

        <>
          {isSmaller ? (
            <Dropdown
              placement="bottomLeft"
              overlay={alignmentMenuList}
              trigger={['click']}
              overlayClassName="alignment-dropdown editor-toolbar-dropdown"
              className="rich-editor-list">
              <Button
                className={!!blockType && blockType.match(/list-item/) ? 'toolbar-icon-active' : ''}
                onMouseDown={(e) => e.preventDefault()}>
                <LeftAlignIcon />
                <DropdownIcon className="rich-editor-dropdown-icon" />
              </Button>
            </Dropdown>
          ) : (
            <>
              {ALIGNMENT_STYLES.map((style) => (
                <ToolbarTooltipItem key={style.text} title={style.text}>
                  <a
                    className={`rich-editor-toolbar-icon ${
                      !!blockType && blockType.match(style.style) ? 'true' : ''
                    } ${direction ? `editor-toolbar-direction-${direction}` : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onToggleCustomBockStyle({
                        blockType: style.style,
                        type: style.type,
                      });
                    }}>
                    {style.type === 'editor-direction'
                      ? React.cloneElement(style.icon)
                      : style.icon}
                  </a>
                </ToolbarTooltipItem>
              ))}
            </>
          )}
          {!isSmaller && <Divider type="vertical" />}
        </>,

        <>
          {SPACING_STYLES.map((style) => (
            <ToolbarTooltipItem key={style.text} title={style.text}>
              <a
                key={style.style}
                className={`rich-editor-toolbar-icon ${
                  (style.style === 'right-intent' && blockType?.search('intent-left-') > -1) ||
                  (!!blockType && blockType.match(style.style))
                    ? 'true'
                    : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (style.style !== 'line-height')
                    onToggleCustomBockStyle({
                      blockType: style.style,
                      type: 'intent',
                    });
                }}>
                {style.icon}
              </a>
            </ToolbarTooltipItem>
          ))}
          <Divider type="vertical" />
        </>,

        <>
          <ToolbarTooltipItem title="Clear formatting" className="toolbar-clear">
            <a
              className={`rich-editor-toolbar-icon rich-editor-toolbar-clear`}
              onMouseDown={(e) => {
                e.preventDefault();
                onClearFormatting(null, true);
              }}>
              <ClearFormattingIcon fill="#05034d" />
            </a>
          </ToolbarTooltipItem>
        </>,

        hideTools.includes('split') ? (
          <span />
        ) : (
          <>
            {!isSmaller && <Divider type="vertical" />}
            <ToolbarTooltipItem title="Split Section">
              <a
                className={`rich-editor-toolbar-icon rich-editor-toolbar-clear 
                  ${(selectionActive || isSection) && 'disabled'}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSplitSection();
                }}
                disabled={selectionActive || isSection}>
                <SplitSectionIcon fill="#05034d" />
                <span className="icon-text">{width < 1200 ? 'Split' : 'Split Section'}</span>
              </a>
            </ToolbarTooltipItem>
          </>
        ),
      ]}
    />
  );
};

ExternalToolbar.defaultProps = {
  bodyFont: '',
};

ExternalToolbar.propTypes = {
  language: PropTypes.string.isRequired,
  bodyFont: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Object)]),
  editorState: PropTypes.instanceOf(Object).isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleBlockType: PropTypes.func.isRequired,
  onToggleFontInlineStyle: PropTypes.func.isRequired,
  onToggleCustomBockStyle: PropTypes.func.isRequired,
  onClearFormatting: PropTypes.func.isRequired,
  onSplitSection: PropTypes.func.isRequired,
  user: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
};

export default ExternalToolbar;
